// -----------------------------------------------------------
// FloraIQ - Toast Unificado
// -----------------------------------------------------------

import { useState, useCallback, createContext, useContext } from "react";
import type { ReactNode } from "react";

export type ToastType = "success" | "error" | "warning" | "info";

interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  showToast: (type: ToastType, message: string) => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

const toastIcons: Record<ToastType, string> = {
  success: "ri-checkbox-circle-line",
  error: "ri-close-circle-line",
  warning: "ri-error-warning-line",
  info: "ri-information-line",
};

const toastStyles: Record<ToastType, string> = {
  success: "bg-primary-50 border-primary-300 text-primary-800",
  error: "bg-danger-50 border-danger-300 text-danger-800",
  warning: "bg-warning-50 border-warning-300 text-warning-800",
  info: "bg-accent-50 border-accent-300 text-accent-800",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  let nextId = 0;

  const showToast = useCallback((type: ToastType, message: string) => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-center gap-2 px-4 py-3 rounded-card border shadow-sm text-sm animate-in slide-in-from-right ${toastStyles[toast.type]}`}
            role="alert"
          >
            <i className={`${toastIcons[toast.type]} text-base`}></i>
            <span className="flex-1">{toast.message}</span>
            <button
              onClick={() => dismissToast(toast.id)}
              className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-black/10 transition-colors cursor-pointer shrink-0"
              aria-label="Fechar notificação"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}